import {
  Loader2Icon,
  MaximizeIcon,
  ZoomInIcon,
  ZoomOutIcon
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import '@/lib/pdfWorker';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { Button } from '@/components/ui/button';

interface PdfViewerDialogProps {
  description?: string;
  filename?: null | string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  pdfData: null | string;
  title?: string;
}

export function PdfViewerDialog({
  description = 'View report content',
  filename,
  onOpenChange,
  open,
  pdfData,
  title = 'Report Viewer'
}: PdfViewerDialogProps) {
  const [numPages, setNumPages] = useState<null | number>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<null | string>(null);
  const [baseWidth, setBaseWidth] = useState(1200);
  const [zoomLevel, setZoomLevel] = useState(1);
  const viewerContainerRef = useRef<HTMLDivElement | HTMLElement | null>(null);

  const pageWidth = baseWidth * zoomLevel;
  const normalizedPdfData = normalizeBase64(pdfData);
  const pdfFile = normalizedPdfData
    ? {
        data: Uint8Array.from(atob(normalizedPdfData), c => c.charCodeAt(0))
      }
    : null;

  useEffect(() => {
    const updateWidth = () => {
      const container = viewerContainerRef.current;
      if (!container) return;
      const width = container.clientWidth;
      if (!width) return;

      const nextWidth = Math.max(320, Math.min(width - 24, 1400));
      setBaseWidth(prev => (Math.abs(prev - nextWidth) > 2 ? nextWidth : prev));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    if (viewerContainerRef.current) {
      observer.observe(viewerContainerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        setZoomLevel(prev => Math.min(prev + 0.25, 3));
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
      }
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        setZoomLevel(1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleClose = () => {
    setNumPages(null);
    setLoading(true);
    setError(null);
    setZoomLevel(1);
    onOpenChange(false);
  };

  return (
    <Dialog
      onOpenChange={nextOpen => {
        if (!nextOpen) {
          handleClose();
        }
      }}
      open={open}
    >
      <DialogContent
        aria-describedby={undefined}
        className="h-[calc(100dvh-2rem)] w-[min(1400px,calc(100vw-2rem))] max-w-none gap-0 overflow-hidden p-0 outline-none"
        showCloseButton={false}
      >
        <DialogHeader className="flex-shrink-0 border-b px-6 pt-6 pb-4">
          <div className="flex items-center justify-between pr-10">
            <div>
              <DialogTitle>{filename || title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 rounded-lg border bg-white px-2 py-1 dark:bg-gray-800">
                <Button
                  className="h-8 w-8 p-0"
                  disabled={zoomLevel <= 0.5}
                  onClick={() => {
                    setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
                  }}
                  size="sm"
                  title="Zoom Out (Ctrl/Cmd + -)"
                  variant="ghost"
                >
                  <ZoomOutIcon className="h-4 w-4" />
                </Button>
                <span className="min-w-[60px] text-center text-sm font-medium">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <Button
                  className="h-8 w-8 p-0"
                  disabled={zoomLevel >= 3}
                  onClick={() => {
                    setZoomLevel(prev => Math.min(prev + 0.25, 3));
                  }}
                  size="sm"
                  title="Zoom In (Ctrl/Cmd + +)"
                  variant="ghost"
                >
                  <ZoomInIcon className="h-4 w-4" />
                </Button>
                <Button
                  className="h-8 w-8 p-0"
                  disabled={zoomLevel === 1}
                  onClick={() => {
                    setZoomLevel(1);
                  }}
                  size="sm"
                  title="Reset Zoom (Ctrl/Cmd + 0)"
                  variant="ghost"
                >
                  <MaximizeIcon className="h-4 w-4" />
                </Button>
              </div>
              <Button
                className="h-8"
                onClick={handleClose}
                size="sm"
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogHeader>
        <DialogBody>
          <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <div
              className="flex min-h-full items-start justify-center overflow-auto rounded-md bg-gray-100 p-4 dark:bg-gray-900"
              ref={viewerContainerRef}
            >
              {!pdfFile ? (
                <div className="flex h-full items-center justify-center">
                  <p className="text-muted-foreground">No PDF data available</p>
                </div>
              ) : error ? (
                <div className="flex h-full items-center justify-center">
                  <p className="text-destructive">{error}</p>
                </div>
              ) : (
                <div className="flex w-full flex-col items-center gap-4">
                  <Document
                    error={
                      <div className="flex items-center justify-center p-8">
                        <p className="text-destructive">
                          Failed to load PDF document
                        </p>
                      </div>
                    }
                    file={pdfFile}
                    loading={
                      <div className="flex items-center justify-center p-8">
                        <Loader2Icon className="text-muted-foreground h-8 w-8 animate-spin" />
                      </div>
                    }
                    onLoadError={() => {
                      setError('Failed to load PDF');
                      setLoading(false);
                    }}
                    onLoadSuccess={({ numPages: loadedPages }) => {
                      setNumPages(loadedPages);
                      setLoading(false);
                      setError(null);
                    }}
                  >
                    {numPages &&
                      Array.from({ length: numPages }, (_, i) => i + 1).map(
                        pageNum => (
                          <div
                            className="mb-4"
                            key={`page-wrapper-${pageNum}`}
                          >
                            <Page
                              className="shadow-lg"
                              error={
                                <div className="border-destructive flex min-h-[400px] items-center justify-center rounded border p-4">
                                  <p className="text-destructive text-sm">
                                    Failed to load page {pageNum}
                                  </p>
                                </div>
                              }
                              key={`page-${pageNum}-${pageWidth}`}
                              pageNumber={pageNum}
                              renderAnnotationLayer
                              renderTextLayer
                              width={pageWidth}
                            />
                          </div>
                        )
                      )}
                  </Document>
                  {loading && !error && (
                    <div className="text-muted-foreground text-sm">
                      Loading PDF...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function normalizeBase64(data: null | string): null | string {
  if (!data) {
    return null;
  }

  let value = data.trim();
  const dataPrefixIndex = value.indexOf('base64,');
  if (dataPrefixIndex >= 0) {
    value = value.slice(dataPrefixIndex + 'base64,'.length);
  }

  // Some backends may wrap bytes values as b'...'
  if (value.startsWith("b'") || value.startsWith('b"')) {
    value = value.slice(2);
    if (value.endsWith("'") || value.endsWith('"')) {
      value = value.slice(0, -1);
    }
  }

  return value.replace(/\s/g, '');
}
